import { supabase } from '@/integrations/supabase/client';

export type ErrorType = 'react_error' | 'api_error' | 'network_error' | 'javascript_error' | 'promise_rejection';
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical';

export interface ErrorLogData {
  error_type: ErrorType;
  error_message: string;
  error_stack?: string;
  component_stack?: string;
  url?: string;
  user_agent?: string;
  additional_data?: Record<string, any>;
  severity?: ErrorSeverity;
}

class ErrorLogger {
  private async getCurrentUser() {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    } catch {
      return null;
    }
  }

  async logError(errorData: ErrorLogData) {
    try {
      const user = await this.getCurrentUser();
      
      const logEntry = {
        user_id: user?.id || null,
        error_type: errorData.error_type,
        error_message: errorData.error_message,
        error_stack: errorData.error_stack || null,
        component_stack: errorData.component_stack || null,
        url: errorData.url || window.location.href,
        user_agent: errorData.user_agent || navigator.userAgent,
        additional_data: errorData.additional_data || {},
        severity: errorData.severity || 'medium'
      };

      const { error } = await supabase
        .from('error_logs')
        .insert(logEntry);

      if (error) {
        console.error('Failed to log error to database:', error);
      }
    } catch (err) {
      console.error('Error logger failed:', err);
    }
  }

  async logReactError(error: Error, errorInfo: { componentStack: string }) {
    await this.logError({
      error_type: 'react_error',
      error_message: error.message,
      error_stack: error.stack,
      component_stack: errorInfo.componentStack,
      severity: 'high',
      additional_data: {
        errorName: error.name,
        componentStack: errorInfo.componentStack
      }
    });
  }

  async logApiError(error: any, endpoint: string, method: string) {
    const severity: ErrorSeverity = error.status >= 500 ? 'high' : 'medium';
    
    await this.logError({
      error_type: 'api_error',
      error_message: error.message || 'API request failed',
      error_stack: error.stack,
      severity,
      additional_data: {
        endpoint,
        method,
        status: error.status,
        statusText: error.statusText,
        response: error.response
      }
    });
  }

  async logNetworkError(error: any, url: string) {
    await this.logError({
      error_type: 'network_error',
      error_message: error.message || 'Network request failed',
      error_stack: error.stack,
      severity: 'medium',
      additional_data: {
        url,
        networkError: true
      }
    });
  }

  async logJavaScriptError(error: ErrorEvent) {
    await this.logError({
      error_type: 'javascript_error',
      error_message: error.message,
      error_stack: error.error?.stack,
      severity: 'medium',
      additional_data: {
        filename: error.filename,
        lineno: error.lineno,
        colno: error.colno
      }
    });
  }

  async logPromiseRejection(event: PromiseRejectionEvent) {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    
    await this.logError({
      error_type: 'promise_rejection',
      error_message: error.message || 'Unhandled promise rejection',
      error_stack: error.stack,
      severity: 'medium',
      additional_data: {
        reason: event.reason,
        promiseRejection: true
      }
    });
  }
}

export const errorLogger = new ErrorLogger();